import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";
import {
  CurrencyConverterService,
  CurrencyCode,
} from "@/services/currencyConvert";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import { formatNumber } from "@/utils/formatNumber";

interface CurrencyContextValue {
  selectedCurrency: CurrencyCode;
  setSelectedCurrency: (currency: CurrencyCode) => void;
  exchangeRates: Record<string, number> | null;
  isLoadingRates: boolean;
  convert: (amount: number | string, fromCurrency?: string) => number;
  formatPrice: (amount: number | string, fromCurrency?: string) => string;
  refreshRates: () => Promise<void>;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(
  undefined,
);

const RATE_CACHE_KEY = "fnp_exchange_rates";
const LEGACY_RATE_CACHE_KEY = "fnp_exchange_rate_usd_ngn";
const RATE_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

interface CachedRates {
  rates: Record<string, number>;
  base: string;
  timestamp: number;
}

function getCachedRates(): CachedRates | null {
  if (typeof window === "undefined") return null;

  // Preferred cache shape: full rates map.
  const raw = localStorage.getItem(RATE_CACHE_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as CachedRates;
      if (
        parsed &&
        parsed.rates &&
        parsed.timestamp &&
        Date.now() - parsed.timestamp <= RATE_CACHE_TTL_MS
      ) {
        return parsed;
      }
    } catch {
      // fall through to legacy migration
    }
  }

  // Backward compatibility: migrate the legacy single USD/NGN rate cache.
  const legacy = localStorage.getItem(LEGACY_RATE_CACHE_KEY);
  if (legacy) {
    try {
      const parsed = JSON.parse(legacy) as { rate: number; timestamp: number };
      if (
        parsed &&
        parsed.rate &&
        parsed.timestamp &&
        Date.now() - parsed.timestamp <= RATE_CACHE_TTL_MS
      ) {
        return {
          rates: { USD: 1, NGN: parsed.rate },
          base: "USD",
          timestamp: parsed.timestamp,
        };
      }
    } catch {
      return null;
    }
  }

  return null;
}

function setCachedRates(cached: CachedRates) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(RATE_CACHE_KEY, JSON.stringify(cached));
  } catch {
    // ignore storage errors
  }
}

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedCurrency, setSelectedCurrencyState] =
    useState<CurrencyCode>("NGN");
  const [exchangeRates, setExchangeRates] = useState<Record<
    string,
    number
  > | null>(null);
  const [isLoadingRates, setIsLoadingRates] = useState(false);

  const refreshRates = async () => {
    setIsLoadingRates(true);
    try {
      const rates = await CurrencyConverterService.getNgnBaseRates();
      setExchangeRates(rates);
      setCachedRates({ rates, base: "NGN", timestamp: Date.now() });
      console.log(
        "[CurrencyContext] NGN-base exchange rates refreshed and cached:",
        rates,
      );
    } catch (error: any) {
      const cached = getCachedRates();
      if (cached) {
        setExchangeRates(cached.rates);
        console.warn(
          "[CurrencyContext] Using cached exchange rates due to fetch failure:",
          cached.rates,
        );
      } else {
        //toast.error("Exchange rates unavailable. Showing prices in original currency.");
        console.error(
          "[CurrencyContext] Exchange rates unavailable and no cache found.",
        );
      }
    } finally {
      setIsLoadingRates(false);
    }
  };

  useEffect(() => {
    const cached = getCachedRates();
    if (cached) {
      setExchangeRates(cached.rates);
      console.log(
        "[CurrencyContext] Loaded exchange rates from cache:",
        cached.rates,
      );
    }
    // Always refresh in the background so the rates stay current (and so GBP,
    // which may be absent from a legacy single-currency cache, is resolved).
    refreshRates();
  }, []);

  const setSelectedCurrency = (currency: CurrencyCode) => {
    setSelectedCurrencyState(currency);
  };

  const convert = (amount: number | string, fromCurrency?: string): number => {
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    if (!Number.isFinite(numAmount)) return 0;
    if (!exchangeRates) return numAmount;
    return CurrencyConverterService.convert(
      numAmount,
      fromCurrency ?? "",
      selectedCurrency,
      exchangeRates,
    );
  };

  const formatPrice = (
    amount: number | string,
    fromCurrency?: string,
  ): string => {
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    if (!Number.isFinite(numAmount))
      return `${getCurrencySymbol(selectedCurrency)} 0`;

    const normalizedFrom = (fromCurrency || "").toUpperCase();
    if (normalizedFrom && normalizedFrom === selectedCurrency) {
      const symbol = getCurrencySymbol(selectedCurrency);
      const formatted = formatNumber(numAmount);
      return `${symbol} ${formatted}`;
    }

    const converted = convert(numAmount, fromCurrency);
    const symbol = getCurrencySymbol(selectedCurrency);
    const formatted = formatNumber(converted);
    return `${symbol} ${formatted}`;
  };

  const value = useMemo(
    () => ({
      selectedCurrency,
      setSelectedCurrency,
      exchangeRates,
      isLoadingRates,
      convert,
      formatPrice,
      refreshRates,
    }),
    [selectedCurrency, exchangeRates, isLoadingRates],
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export function useCurrency(): CurrencyContextValue {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
}
